import { Checkbox } from 'antd';
import { CURRENCY_OPTIONS, DEFAULT_CURRENCY, formatMoney } from '@/constants/currency';
import type { BaseColumn } from './RateCards.types';
import { optionalRateCardTagsRule, optionalRateCardTextRule } from './rateCardFormRules';

export enum Column {
  CHANNEL = 'channel',
  PRODUCT = 'product',
  TACTIC = 'tactic',
  MIN_MONTH_SPENT = 'minMonthSpent',
  BUYING_METRIC = 'buyingMetric',
  CURRENCY = 'currency',
  BUY_RATE = 'buyRate',
  SELL_RATE = 'sellRate',
  MEASURE = 'measure',
  BENCHMARK = 'benchmark',
  FORMAT = 'format',
  COSTS = 'costs',
  COSTS_FEE = 'costsFee',
  NOTES = 'notes',
  ACTIONS = 'actions'
}

export const COLUMNS: Record<Column, BaseColumn> = {
  [Column.CHANNEL]: {
    i18nKey: 'channel',
    dataIndex: 'channel',
    className: 'channel',
    rules: [{ required: true, pattern: /^[A-Za-z0-9@#$%^&\\\-_ +–=<>()/!*,.?;:]+$/ }],
    sorter: true,
    type: 'text',
    editable: true,
    ellipsis: true,
    key: 'channel'
  },
  [Column.PRODUCT]: {
    i18nKey: 'product',
    dataIndex: 'product',
    className: 'product',
    type: 'autocomplete',
    optionsKey: 'products',
    rules: [optionalRateCardTextRule],
    editable: true,
    ellipsis: true,
    key: 'product',
    render: (_, { product }) => product || 'N/A'
  },
  [Column.TACTIC]: {
    i18nKey: 'tactic',
    dataIndex: 'tactic',
    className: 'tactic',
    type: 'autocomplete',
    optionsKey: 'tactics',
    rules: [optionalRateCardTextRule],
    editable: true,
    ellipsis: true,
    key: 'tactic',
    render: (_, { tactic }) => tactic || 'N/A'
  },
  [Column.MIN_MONTH_SPENT]: {
    i18nKey: 'monthlySpent',
    dataIndex: 'monthlySpent',
    className: 'monthly-spent',
    type: 'pound',
    align: 'right',
    editable: true,
    ellipsis: true,
    key: 'minMonthSpent',
    render: (_, { monthlySpent, currency }) => formatMoney(monthlySpent, currency)
  },
  [Column.BUYING_METRIC]: {
    i18nKey: 'buyingMetric',
    dataIndex: 'buyingMetric',
    className: 'buying-metric',
    type: 'select',
    optionsKey: 'buyingMetrics',
    align: 'right',
    editable: true,
    ellipsis: true,
    key: 'buyingMetric',
    render: (_, { buyingMetric }) => buyingMetric || 'N/A'
  },
  [Column.CURRENCY]: {
    i18nKey: 'currency',
    dataIndex: 'currency',
    className: 'currency',
    type: 'select',
    selectOptions: CURRENCY_OPTIONS,
    rules: [{ required: true, message: 'Currency is required' }],
    editable: true,
    ellipsis: true,
    key: 'currency',
    render: (_, { currency }) => currency ?? DEFAULT_CURRENCY
  },
  [Column.BUY_RATE]: {
    i18nKey: 'buyRate',
    dataIndex: 'buyRate',
    className: 'buy-rate',
    type: 'pound',
    align: 'right',
    editable: true,
    ellipsis: true,
    key: 'buyRate',
    render: (_, { buyRate, currency }) => formatMoney(buyRate, currency)
  },
  [Column.SELL_RATE]: {
    i18nKey: 'sellRate',
    dataIndex: 'sellRate',
    className: 'sell-rate',
    type: 'pound',
    align: 'right',
    editable: true,
    ellipsis: true,
    key: 'sellRate',
    render: (_, { sellRate, currency }) => formatMoney(sellRate, currency)
  },
  [Column.MEASURE]: {
    i18nKey: 'measure',
    dataIndex: 'kpis',
    optionsKey: 'kpis',
    className: 'measure',
    align: 'right',
    type: 'multiselect',
    editable: true,
    ellipsis: true,
    key: 'measure'
  },
  [Column.BENCHMARK]: {
    i18nKey: 'benchmark',
    dataIndex: 'benchmark',
    className: 'benchmark',
    align: 'right',
    type: 'percent',
    editable: true,
    ellipsis: true,
    key: 'benchmark',
    render: (_, { benchmark }) => (benchmark ? `${benchmark}%` : 'N/A')
  },
  [Column.FORMAT]: {
    i18nKey: 'format',
    dataIndex: 'formats',
    className: 'formats',
    optionsKey: 'formats',
    type: 'multiselect',
    rules: [optionalRateCardTagsRule],
    editable: true,
    ellipsis: true,
    key: 'format'
  },
  [Column.COSTS]: {
    i18nKey: 'costs',
    dataIndex: 'cost',
    className: 'cost',
    type: 'checkbox',
    align: 'right',
    valuePropName: 'checked',
    editable: true,
    ellipsis: true,
    key: 'cost',
    render: (_, { cost }) => <Checkbox disabled checked={cost} />
  },
  [Column.COSTS_FEE]: {
    i18nKey: 'hardCosts',
    dataIndex: 'costsFee',
    className: 'costs-fee',
    type: 'pound',
    align: 'right',
    editable: true,
    ellipsis: true,
    key: 'costsFee',
    render: (_, { costsFee, currency }) => formatMoney(costsFee, currency)
  },
  [Column.NOTES]: {
    i18nKey: 'notes',
    dataIndex: 'notes',
    className: 'notes',
    type: 'text',
    editable: true,
    ellipsis: true,
    key: 'notes',
    render: (_, { notes }) => notes || 'N/A'
  },
  [Column.ACTIONS]: {
    i18nKey: 'actions',
    type: null,
    key: 'actions',
    fixed: 'right',
    width: 150,
    align: 'center',
    editable: false,
    ellipsis: true,
    className: 'actions'
  }
};
